import { accountNumber, defineResource } from '@tnn/core'
import { z } from 'zod'
import { addDays } from '../hr/attendance'
import { departmentStatuses, type nameTitles } from '../hr/resources'
import { bankAccountNumber, banks } from '../party'
import { vehicleTypes } from '../tms/vehicle'

/**
 * Payroll (เงินเดือน): what each person is paid and how, the kinds of money on a payslip, extras
 * and deductions, the trip pay of drivers and their helpers, a pay run with its payslips, and the
 * tax allowances people declare (ล.ย.01). Everyone, monthly staff and day workers alike, is paid in
 * one regular run a month: the salary for the calendar month, the pay that goes by time (day wages,
 * overtime, absence, lateness, trips) up to the cut-off day (`cutoffDay`, the month's end when not
 * set). A special run pays a stretch of days or a one-off (a bonus) to whom it names. Payroll
 * prepares a run, the manager approves it, and its payslips appear in Thananan OS.
 */

/** `monthly`: a salary a month; `daily`: a wage a day worked */
export const payBases = ['monthly', 'daily'] as const
export type PayBasis = (typeof payBases)[number]
/** `regular`: the month's run, everyone with pay; `special`: days or a one-off for whom it names */
export const runKinds = ['regular', 'special'] as const
export type RunKind = (typeof runKinds)[number]
/** Whom a special run pays: everyone with pay, day workers, or the people it lists */
export const specialPeople = ['all', 'daily', 'chosen'] as const
export const payMethods = ['transfer', 'cash'] as const

/**
 * payroll.compensation (ค่าจ้าง): one per employee — the salary or day wage, how it is paid, and
 * whether social security and tax are taken. Payroll only: nobody else sees pay, HR neither.
 */
export const compensation = defineResource({
  app: 'payroll',
  name: 'compensation',
  path: 'compensations',
  schema: z.object({
    employeeId: z.string().min(1),
    /** Empty: by the employee's employment type (daily → daily, else monthly), set by the server */
    payBasis: z.enum(payBases).nullish(),
    /** Baht a month (monthly) or a day (daily) */
    rate: z.number().min(0).max(10_000_000),
    payMethod: z.enum(payMethods).default('transfer'),
    bank: z.enum(banks).nullish(),
    bankAccount: bankAccountNumber,
    bankAccountName: z.string().trim().max(120).nullish(),
    /** An insured person under section 33: 5% of wages to social security each month */
    socialSecurity: z.boolean().default(true),
    withholdTax: z.boolean().default(true),
    /** The security deposit (เงินประกัน) to build up from their pay: none, nothing is taken */
    bondTarget: z.number().min(0).max(1_000_000).nullish(),
    /** Taken each regular run until the target is reached (`bondDue`) */
    bondPerRun: z.number().min(0).max(1_000_000).nullish(),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, search: true, list: { priority: 1 }, form: { span: 12 } },
    payBasis: { filter: true, list: { width: 120, priority: 1 }, form: { span: 6 } },
    rate: { type: 'money', list: { align: 'end', width: 130, priority: 1 }, form: { span: 6 } },
    payMethod: { filter: true, list: { width: 110, priority: 2 }, form: { span: 4, section: 'bank' } },
    bank: {
      type: 'select',
      options: banks,
      optionLabels: 'banks',
      filter: true,
      list: { width: 110, priority: 2 },
      form: { span: 4, section: 'bank' },
    },
    bankAccount: { list: { priority: 3 }, form: { span: 4, section: 'bank' }, import: { value: accountNumber } },
    bankAccountName: { list: false, form: { span: 12, section: 'bank' } },
    socialSecurity: { list: { width: 120, priority: 3 }, form: { span: 6, section: 'deductions' } },
    withholdTax: { list: { width: 120, priority: 3, hidden: true }, form: { span: 6, section: 'deductions' } },
    bondTarget: { type: 'money', list: { align: 'end', width: 130, priority: 3, hidden: true }, form: { span: 6, section: 'bond' } },
    bondPerRun: { type: 'money', list: { align: 'end', width: 130, priority: 3, hidden: true }, form: { span: 6, section: 'bond' } },
    /** The employee's branch (server-set) */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'deductions' } },
  },
  title: 'employeeId',
  columns: [
    'employeeId',
    'payBasis',
    'rate',
    'payMethod',
    'bank',
    'bankAccount',
    'socialSecurity',
    'withholdTax',
    'bondTarget',
    'bondPerRun',
    'branchId',
  ],
  sections: ['main', 'bank', 'deductions', 'bond'],
  defaultSort: 'employeeId',
  actions: ['read', 'create', 'update', 'delete', 'import', 'export'],
  // One per employee: importing again updates it
  importKey: 'employeeId',
  card: { title: 'employeeId', subtitle: 'payBasis', meta: ['rate', 'bank'] },
})

/** `earning`: adds to pay; `deduction`: taken from it */
export const payItemKinds = ['earning', 'deduction'] as const
export type PayItemKind = (typeof payItemKinds)[number]

/**
 * payroll.payItem (รายการเงินได้และเงินหัก): the kinds of money on a payslip. The system's own
 * (`system`: salary, wage, overtime, trip pay, diligence, absence, social security, tax…) are
 * calculated; Payroll adds the company's (position allowance, phone…). `taxable` and `wage` say what
 * the item counts towards: an earning adds to taxable income and to the wage social security is
 * taken on; a deduction with them subtracts (absence, unpaid leave) — one without is taken after
 * (an advance, social security itself).
 */
export const payItem = defineResource({
  app: 'payroll',
  name: 'payItem',
  path: 'pay-items',
  schema: z.object({
    code: z.string().trim().toUpperCase().min(1).max(20),
    name: z.string().trim().min(1).max(60),
    kind: z.enum(payItemKinds).default('earning'),
    /** Counts in income for withholding tax (40(1)) */
    taxable: z.boolean().default(true),
    /** Counts in the wage social security is taken on */
    wage: z.boolean().default(true),
    /**
     * Paid once, not every run (a bonus, severance, leave paid out): its tax is the difference it
     * makes to the year's, taken in the run that pays it, not spread as if it came every month
     */
    irregular: z.boolean().default(false),
    status: z.enum(departmentStatuses).default('active'),
    sortOrder: z.number().int().min(0).max(999).default(100),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    code: { search: true, list: { width: 130, priority: 2 }, form: { span: 4 } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 } },
    kind: { filter: true, tones: { earning: 'success', deduction: 'warning' }, list: { width: 110, priority: 1 }, form: { span: 4 } },
    taxable: { list: { width: 110, priority: 2 }, form: { span: 4 } },
    wage: { list: { width: 120, priority: 2 }, form: { span: 4 } },
    irregular: { list: { width: 110, priority: 3 }, form: { span: 4 } },
    /** Calculated by the system (server-set): its code and kind stay */
    system: { readOnly: true, type: 'boolean', list: { width: 100, priority: 3 } },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { width: 110, priority: 1 }, form: { span: 4 } },
    sortOrder: { list: false, form: { span: 4 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  columns: ['name', 'code', 'kind', 'taxable', 'wage', 'irregular', 'system', 'status'],
  defaultSort: 'sortOrder',
  importKey: 'code',
  card: { title: 'name', subtitle: 'code', status: 'kind' },
})

/** Codes of the items the system calculates */
export const systemPayItems = [
  'SALARY',
  'WAGE',
  'OT',
  'HOLIDAY',
  'HOLIDAY_OT',
  'TRIP',
  'FUEL',
  'DILIGENCE',
  'LEAVE_PAYOUT',
  'SEVERANCE',
  'ABSENT',
  'UNPAID_LEAVE',
  'LATE',
  'SSO',
  'TAX',
  'BOND',
  'BOND_REFUND',
] as const
export type SystemPayItem = (typeof systemPayItems)[number]

type PayItemValues = Pick<z.infer<typeof payItem.schema>, 'code' | 'name' | 'kind' | 'taxable' | 'wage' | 'sortOrder'> & {
  irregular?: boolean
}

/** The starting set: the system's items, then a few the company may use (payroll edits and adds) */
export const standardPayItems: (PayItemValues & { system: boolean })[] = [
  { code: 'SALARY', name: 'เงินเดือน', kind: 'earning', taxable: true, wage: true, sortOrder: 10, system: true },
  { code: 'WAGE', name: 'ค่าจ้างรายวัน', kind: 'earning', taxable: true, wage: true, sortOrder: 11, system: true },
  { code: 'OT', name: 'ค่าล่วงเวลา', kind: 'earning', taxable: true, wage: true, sortOrder: 20, system: true },
  { code: 'HOLIDAY', name: 'ค่าทำงานในวันหยุด', kind: 'earning', taxable: true, wage: true, sortOrder: 21, system: true },
  { code: 'HOLIDAY_OT', name: 'ค่าล่วงเวลาในวันหยุด', kind: 'earning', taxable: true, wage: true, sortOrder: 22, system: true },
  { code: 'TRIP', name: 'ค่าเที่ยว', kind: 'earning', taxable: true, wage: true, sortOrder: 30, system: true },
  // A delivery run's distance at the fuel rate, for the fuel the driver buys: whether it is taxed is accounting's to say
  { code: 'FUEL', name: 'ค่าน้ำมัน', kind: 'earning', taxable: true, wage: false, sortOrder: 31, system: true },
  {
    code: 'LEAVE_PAYOUT',
    name: 'ค่าจ้างวันพักร้อนที่ไม่ได้ใช้',
    kind: 'earning',
    taxable: true,
    wage: false,
    irregular: true,
    sortOrder: 40,
    system: true,
  },
  // The part over the exemption is taxed; the calculation splits it
  {
    code: 'SEVERANCE',
    name: 'ค่าชดเชยตามกฎหมาย',
    kind: 'earning',
    taxable: true,
    wage: false,
    irregular: true,
    sortOrder: 41,
    system: true,
  },
  {
    code: 'NOTICE_PAY',
    name: 'ค่าจ้างแทนการบอกกล่าวล่วงหน้า',
    kind: 'earning',
    taxable: true,
    wage: false,
    irregular: true,
    sortOrder: 42,
    system: false,
  },
  // The security deposit given back with the final payslip: their own money, not income
  { code: 'BOND_REFUND', name: 'คืนเงินประกัน', kind: 'earning', taxable: false, wage: false, sortOrder: 44, system: true },
  { code: 'BONUS', name: 'โบนัส', kind: 'earning', taxable: true, wage: false, irregular: true, sortOrder: 45, system: false },
  { code: 'POSITION', name: 'ค่าตำแหน่ง', kind: 'earning', taxable: true, wage: true, sortOrder: 50, system: false },
  // By the employee's rates and their months in a row without absence, leave or lateness (hr/diligence.ts)
  { code: 'DILIGENCE', name: 'เบี้ยขยัน', kind: 'earning', taxable: true, wage: true, sortOrder: 51, system: true },
  { code: 'PHONE', name: 'ค่าโทรศัพท์', kind: 'earning', taxable: true, wage: false, sortOrder: 52, system: false },
  { code: 'ABSENT', name: 'หักขาดงาน', kind: 'deduction', taxable: true, wage: true, sortOrder: 60, system: true },
  { code: 'UNPAID_LEAVE', name: 'หักลาไม่รับค่าจ้าง', kind: 'deduction', taxable: true, wage: true, sortOrder: 61, system: true },
  { code: 'LATE', name: 'หักมาสาย', kind: 'deduction', taxable: true, wage: true, sortOrder: 62, system: true },
  { code: 'SSO', name: 'ประกันสังคม', kind: 'deduction', taxable: false, wage: false, sortOrder: 70, system: true },
  { code: 'TAX', name: 'ภาษีหัก ณ ที่จ่าย', kind: 'deduction', taxable: false, wage: false, sortOrder: 71, system: true },
  { code: 'ADVANCE', name: 'หักเงินเบิกล่วงหน้า', kind: 'deduction', taxable: false, wage: false, sortOrder: 80, system: false },
  // Into their security deposit, up to its target (section 10 and 76: counts towards the fifth of pay deductions may take)
  { code: 'BOND', name: 'หักเงินประกัน', kind: 'deduction', taxable: false, wage: false, sortOrder: 81, system: true },
]

export const payRepeats = ['once', 'monthly'] as const

/**
 * payroll.payAdjustment (เงินเพิ่มและเงินหัก): an extra or a deduction for one person, by a pay item.
 * `once`: in the run whose period holds `date` (an advance, a bonus); `monthly`: every month from
 * `date`'s month until `endDate`'s (a position allowance) — for day workers in the run that ends
 * the month. Payroll only.
 */
export const payAdjustment = defineResource({
  app: 'payroll',
  name: 'payAdjustment',
  path: 'pay-adjustments',
  schema: z.object({
    employeeId: z.string().min(1),
    payItemId: z.string().min(1),
    amount: z.number().positive().max(10_000_000),
    repeat: z.enum(payRepeats).default('once'),
    date: z.iso.date(),
    /** monthly: the last month it applies (empty: until removed) */
    endDate: z.iso.date().nullish(),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, search: true, list: { priority: 1 }, form: { span: 6 } },
    payItemId: {
      type: 'ref',
      // The system's items too (overtime owed from last month); social security and tax are refused
      ref: { resource: 'payroll.payItem', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    amount: { type: 'money', list: { align: 'end', width: 130, priority: 1 }, form: { span: 4 } },
    repeat: { filter: true, list: { width: 120, priority: 2 }, form: { span: 4 } },
    date: { filter: true, list: { width: 120, priority: 1 }, form: { span: 4 } },
    endDate: { list: { width: 120, priority: 3 }, form: { span: 4 } },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    createdByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'payItemId',
  columns: ['employeeId', 'payItemId', 'amount', 'repeat', 'date', 'endDate', 'branchId', 'createdByUserId'],
  defaultSort: '-date',
  actions: ['read', 'create', 'update', 'delete', 'import', 'export'],
  card: { title: 'employeeId', subtitle: 'payItemId', meta: ['amount', 'date'] },
})

/**
 * payroll.tripRate (อัตราค่าเที่ยว): the pay for one delivered job, by the vehicle and the distance —
 * this row's amounts from `fromKm` up to the next row's. No vehicle type: any vehicle without a
 * row of its own. A driver and each helper on the job are paid their own amount.
 */
export const tripRate = defineResource({
  app: 'payroll',
  name: 'tripRate',
  path: 'trip-rates',
  schema: z.object({
    vehicleType: z.enum(vehicleTypes).nullish(),
    fromKm: z.number().min(0).max(5000).default(0),
    driverAmount: z.number().min(0).max(100_000),
    helperAmount: z.number().min(0).max(100_000).default(0),
    notes: z.string().max(300).nullish(),
  }),
  fields: {
    vehicleType: {
      type: 'select',
      options: vehicleTypes,
      optionLabels: 'tms:vehicle.options.type',
      filter: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    fromKm: { list: { align: 'end', width: 120, priority: 1 }, form: { span: 6 } },
    driverAmount: { type: 'money', list: { align: 'end', width: 130, priority: 1 }, form: { span: 6 } },
    helperAmount: { type: 'money', list: { align: 'end', width: 130, priority: 1 }, form: { span: 6 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'vehicleType',
  columns: ['vehicleType', 'fromKm', 'driverAmount', 'helperAmount'],
  defaultSort: 'vehicleType,fromKm',
  actions: ['read', 'create', 'update', 'delete', 'import', 'export'],
})

/**
 * payroll.fuelRate (ค่าน้ำมันต่อ กม.): baht a kilometre of a delivery run (last mile, tms.deliveryRun) by the vehicle,
 * its distance from the odometer — the staff driver's fuel pay (ค่าน้ำมัน) on the payslip, a contract driver's fuel line
 * on their bill. No vehicle type: any vehicle without a row of its own.
 */
export const fuelRate = defineResource({
  app: 'payroll',
  name: 'fuelRate',
  path: 'fuel-rates',
  schema: z.object({
    vehicleType: z.enum(vehicleTypes).nullish(),
    perKm: z.number().min(0).max(1000),
    notes: z.string().max(300).nullish(),
  }),
  fields: {
    vehicleType: {
      type: 'select',
      options: vehicleTypes,
      optionLabels: 'tms:vehicle.options.type',
      filter: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    perKm: { type: 'money', list: { align: 'end', width: 130, priority: 1 }, form: { span: 6 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'vehicleType',
  columns: ['vehicleType', 'perKm'],
  defaultSort: 'vehicleType',
  actions: ['read', 'create', 'update', 'delete', 'import', 'export'],
})

export const payrollRunStatuses = ['draft', 'pending', 'approved', 'paid'] as const
export type PayrollRunStatus = (typeof payrollRunStatuses)[number]

/**
 * payroll.payrollRun (งวดเงินเดือน): a run, its days and pay day. A regular one is a month's (`month`):
 * the salary for that month, what goes by time from the day after the last regular run's cut-off to
 * this one's (`periodStart`–`periodEnd`). A special one pays the days it spans (`countsTime`) or only
 * what payroll adds (a bonus), to `people`. Made by payroll, it calculates a payslip for each; payroll
 * checks them, adds and edits lines, calculates again after late changes (leave, overtime, jobs) and
 * submits it (`pending`, locked); the manager approves (the payslips appear in Thananan OS) and
 * payroll records it paid. Reopening a pending or approved run (not a paid one) takes it back to draft.
 */
export const payrollRun = defineResource({
  app: 'payroll',
  name: 'payrollRun',
  path: 'payroll',
  schema: z.object({
    kind: z.enum(runKinds).default('regular'),
    /** The month it pays (YYYY-MM): a regular run's salary and adjustments; social security goes by it (a special run: its pay day's) */
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional(),
    periodStart: z.iso.date(),
    periodEnd: z.iso.date(),
    payDate: z.iso.date(),
    /** A special run's own name ("โบนัสสิ้นปี"); without it, its days */
    title: z.string().trim().max(120).nullish(),
    people: z.enum(specialPeople).default('all'),
    /** Whom a special run pays when `people` is `chosen` */
    employeeIds: z.array(z.string()).default([]),
    /** A special run pays the time in its days (day wages, overtime, absence, trips); off: only lines added to it */
    countsTime: z.boolean().default(true),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    /** "ธันวาคม 2569", "งวดพิเศษ 26–31 ธันวาคม 2569", or a special run's title (server-set) */
    name: { readOnly: true, search: true, list: { priority: 1 } },
    kind: { filter: true, list: { width: 120, priority: 1 }, form: { span: 4, custom: true } },
    month: { filter: true, list: { width: 120, priority: 2, hidden: true }, form: { span: 4, custom: true } },
    periodStart: { filter: true, list: { width: 120, priority: 2 }, form: { span: 4, custom: true } },
    periodEnd: { list: { width: 120, priority: 2 }, form: { span: 4, custom: true } },
    title: { list: false, form: { span: 12, custom: true } },
    people: { list: false, form: { span: 6, custom: true } },
    employeeIds: { type: 'multiselect', ref: { resource: 'hr.employee' }, list: false, form: { span: 12, custom: true } },
    countsTime: { list: false, form: { span: 6, custom: true } },
    payDate: { filter: true, list: { width: 120, priority: 1 }, form: { span: 4 } },
    status: {
      readOnly: true,
      type: 'select',
      options: payrollRunStatuses,
      filter: true,
      tones: { draft: 'neutral', pending: 'warning', approved: 'info', paid: 'success' },
      list: { width: 120, priority: 1 },
    },
    employeeCount: { readOnly: true, type: 'integer', list: { align: 'end', width: 100, priority: 2 } },
    gross: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 2 } },
    deductions: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 3 } },
    net: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 1 } },
    calculatedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    submittedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    submittedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    approvedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    approvedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    paidAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    createdByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  columns: ['name', 'kind', 'payDate', 'status', 'employeeCount', 'gross', 'deductions', 'net', 'periodStart', 'periodEnd'],
  defaultSort: '-periodStart',
  // calculate: again from the data (draft); submit: for approval; approve / reopen: the manager; pay: recorded paid
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'calculate', 'submit', 'approve', 'reopen', 'pay'],
  card: { title: 'name', subtitle: 'kind', meta: ['payDate', 'net'], status: 'status' },
})

/**
 * A day a day worker was paid for ahead, by their schedule, because it was not over when the run was calculated: the
 * days it counted (1, a half on unpaid leave, 0) at the wage then. The next run that counts them settles them by what
 * happened (absent: taken back; more worked: paid).
 */
export interface PayAhead {
  date: string
  days: number
  rate: number
}

/** `auto`: calculated (again on each calculation); `adjustment`: from payroll.payAdjustment; `manual`: added on the payslip */
export const payLineSources = ['auto', 'adjustment', 'manual'] as const

/**
 * One line of a payslip. Payroll changes a calculated or adjustment line by its `key` (an override,
 * kept when the run is calculated again; 0 drops the line) and adds manual ones.
 */
export interface PayLine {
  /** Stable within the payslip: the code for a calculated line (`HOLIDAY:2` for a second one), the adjustment's id, a manual line's own */
  key: string
  /** The pay item's code (SALARY, OT, POSITION…) and its name when calculated */
  code: string
  name: string
  kind: PayItemKind
  taxable: boolean
  wage: boolean
  /** Paid once (a bonus, severance): its tax is not spread over the year */
  irregular?: boolean
  amount: number
  /** How many and of what at which rate: 22 days × 500, 6 hours × 93.75 */
  qty?: number | null
  unit?: 'day' | 'hour' | 'minute' | 'trip' | 'km' | null
  rate?: number | null
  source: (typeof payLineSources)[number]
  /** The adjustment it came from */
  adjustmentId?: string | null
  /** What the system calculated, when payroll set another amount */
  calculated?: number | null
  note?: string | null
}

/** One delivered job in a payslip's trip pay, or a trip between branches (line haul) driven */
export interface PayTrip {
  /** The job (tms.shipment) */
  shipmentId?: string
  /** Or the trip between branches (tms.trip), rated by its route */
  tripId?: string
  number: string
  date: string
  /** On the job as the driver or a helper (a trip between branches: its driver) */
  role: 'driver' | 'helper'
  vehicleType: string | null
  distanceKm: number | null
  /** A trip between branches: from its origin to its last stop, by branch code (`BKK1 → DC-BKK`) */
  route?: string
  /** By the rates */
  rated: number | null
  /** Payroll's amount instead, kept when calculating again */
  amount: number
  override?: boolean
}

/** Which job or trip a payslip's trip pay is for */
export const payTripRef = (trip: Pick<PayTrip, 'shipmentId' | 'tripId'>) => trip.tripId ?? trip.shipmentId ?? ''

/** One delivery run (last mile) in a payslip's fuel pay (ค่าน้ำมัน): its distance at the vehicle's rate a km */
export interface PayRun {
  runId: string
  number: string
  date: string
  vehicleType: string | null
  /** From the odometer readings; null until both are recorded */
  distanceKm: number | null
  /** Baht a km by the fuel rates; null: none for the vehicle */
  perKm: number | null
  /** By the rates; null while either is missing */
  rated: number | null
  /** Payroll's amount instead, kept when calculating again */
  amount: number
  override?: boolean
}

/** Warnings payroll sees on a payslip before approving */
export const payslipProblems = [
  'noBankAccount',
  'noTrips',
  'unratedTrip',
  'noDistance',
  'unratedRun',
  'overDeduction',
  'negativeNet',
  'incomplete',
  'noIdCard',
  'cashHeld',
  'bondNoAccount',
  'bondLate',
] as const
export type PayslipProblem = (typeof payslipProblems)[number]

/**
 * payroll.payslip (สลิปเงินเดือน): one person's pay in a run — its lines, totals and the year so far
 * for tax. Calculated by the server; payroll adds lines and trip amounts through /api/payroll. The
 * employee sees their own in Thananan OS once the run is approved.
 */
export const payslip = defineResource({
  app: 'payroll',
  name: 'payslip',
  path: 'payslips',
  schema: z.object({
    runId: z.string().min(1),
    employeeId: z.string().min(1),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    runId: { type: 'ref', ref: { resource: 'payroll.payrollRun' }, filter: true, list: { priority: 2 }, form: { span: 6 } },
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, search: true, list: { priority: 1 }, form: { span: 6 } },
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    payBasis: {
      readOnly: true,
      type: 'select',
      options: payBases,
      optionLabels: 'payroll:compensation.options.payBasis',
      list: { width: 110, priority: 3 },
    },
    rate: { readOnly: true, type: 'money', list: { align: 'end', width: 120, priority: 3, hidden: true } },
    /** Days paid (day workers, those ahead too) or employed (monthly staff) */
    days: { readOnly: true, type: 'number', list: { align: 'end', width: 90, priority: 3 } },
    /** The days counted by what happened (from the day after this person's last such day in an earlier run) */
    actualFrom: { readOnly: true, type: 'date', list: { priority: 3, hidden: true } },
    actualThrough: { readOnly: true, type: 'date', list: { priority: 3, hidden: true } },
    /** Days paid ahead by the schedule (`PayAhead`), settled by the next run */
    ahead: { readOnly: true, type: 'json', list: false, export: false },
    gross: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 1 } },
    deductions: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 2 } },
    net: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 1 } },
    taxable: { readOnly: true, type: 'money', list: false },
    ssoWage: { readOnly: true, type: 'money', list: false },
    sso: { readOnly: true, type: 'money', list: { align: 'end', width: 110, priority: 3, hidden: true } },
    tax: { readOnly: true, type: 'money', list: { align: 'end', width: 110, priority: 3, hidden: true } },
    lines: { readOnly: true, type: 'json', list: false, export: false },
    trips: { readOnly: true, type: 'json', list: false, export: false },
    runs: { readOnly: true, type: 'json', list: false, export: false },
    /** Calculated lines payroll dropped (`{ key, code, name, calculated }`), to bring back */
    removed: { readOnly: true, type: 'json', list: false, export: false },
    problems: { readOnly: true, type: 'json', list: false, export: false },
    /** The year to date before this payslip (tax) */
    ytdTaxable: { readOnly: true, type: 'money', list: false },
    ytdTax: { readOnly: true, type: 'money', list: false },
    ytdSso: { readOnly: true, type: 'money', list: false },
    payMethod: {
      readOnly: true,
      type: 'select',
      options: payMethods,
      optionLabels: 'payroll:compensation.options.payMethod',
      list: { width: 110, priority: 3, hidden: true },
    },
    bank: {
      readOnly: true,
      type: 'select',
      options: banks,
      optionLabels: 'banks',
      list: { priority: 3, hidden: true },
    },
    bankAccount: { readOnly: true, list: { priority: 3, hidden: true } },
    /** The run's (server-set) */
    status: {
      readOnly: true,
      type: 'select',
      options: payrollRunStatuses,
      optionLabels: 'payroll:payrollRun.options.status',
      filter: true,
      tones: { draft: 'neutral', pending: 'warning', approved: 'info', paid: 'success' },
      list: { width: 120, priority: 2 },
    },
    payDate: { readOnly: true, type: 'date', filter: true, list: { width: 120, priority: 1 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'employeeId',
  columns: ['employeeId', 'runId', 'payDate', 'days', 'gross', 'deductions', 'net', 'status', 'payBasis', 'sso', 'tax', 'branchId'],
  defaultSort: '-payDate',
  actions: ['read', 'update', 'export', 'print'],
  ownerField: 'userId',
  card: { title: 'employeeId', subtitle: 'runId', meta: ['net'], status: 'status' },
})

/**
 * payroll.taxDeclaration (ค่าลดหย่อนภาษี, ล.ย.01): what someone declares for the year's withholding
 * tax — spouse, children, parents, insurance, fund, home loan. Filled by the employee in My
 * Thananan (payroll may too); the personal allowance, the expense deduction and social security are
 * counted without it.
 */
export const taxDeclaration = defineResource({
  app: 'payroll',
  name: 'taxDeclaration',
  path: 'tax-declarations',
  schema: z.object({
    employeeId: z.string().min(1),
    year: z.number().int().min(2020).max(2100),
    /** A spouse without income */
    spouse: z.boolean().default(false),
    /** Children (30,000 each) */
    children: z.number().int().min(0).max(20).default(0),
    /** Of them, the second and later born from 2018 (60,000 each instead) */
    childrenFrom2018: z.number().int().min(0).max(20).default(0),
    /** Parents and the spouse's parents aged 60 and over with little income (30,000 each, up to 4) */
    parents: z.number().int().min(0).max(4).default(0),
    /** Disabled or incapacitated dependants (60,000 each) */
    disabled: z.number().int().min(0).max(10).default(0),
    lifeInsurance: z.number().min(0).max(10_000_000).default(0),
    healthInsurance: z.number().min(0).max(10_000_000).default(0),
    parentsHealthInsurance: z.number().min(0).max(10_000_000).default(0),
    providentFund: z.number().min(0).max(10_000_000).default(0),
    homeLoanInterest: z.number().min(0).max(10_000_000).default(0),
    /** Anything else allowed (RMF, ThaiESG, donations…), the total for the year */
    other: z.number().min(0).max(10_000_000).default(0),
    /**
     * Payroll's: what this company paid and took this year before the person's first payslip in the
     * system (starting mid-year) — counted in the year to date and on the 50 ทวิ
     */
    openingIncome: z.number().min(0).max(100_000_000).default(0),
    openingTax: z.number().min(0).max(100_000_000).default(0),
    openingSso: z.number().min(0).max(1_000_000).default(0),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, search: true, list: { priority: 1 }, form: { span: 8 } },
    year: { type: 'integer', filter: true, list: { width: 100, priority: 1 }, form: { span: 4 } },
    spouse: { list: { width: 100, priority: 2 }, form: { span: 12, section: 'family' } },
    children: { type: 'integer', list: { align: 'end', width: 90, priority: 2 }, form: { span: 4, section: 'family' } },
    childrenFrom2018: { type: 'integer', list: false, form: { span: 4, section: 'family' } },
    parents: { type: 'integer', list: { align: 'end', width: 90, priority: 3 }, form: { span: 4, section: 'family' } },
    disabled: { type: 'integer', list: false, form: { span: 4, section: 'family' } },
    lifeInsurance: { type: 'money', list: false, form: { span: 6, section: 'insurance' } },
    healthInsurance: { type: 'money', list: false, form: { span: 6, section: 'insurance' } },
    parentsHealthInsurance: { type: 'money', list: false, form: { span: 6, section: 'insurance' } },
    providentFund: { type: 'money', list: false, form: { span: 6, section: 'insurance' } },
    homeLoanInterest: { type: 'money', list: false, form: { span: 6, section: 'other' } },
    other: { type: 'money', list: false, form: { span: 6, section: 'other' } },
    openingIncome: { type: 'money', list: { align: 'end', width: 130, priority: 3, hidden: true }, form: { span: 4, section: 'opening' } },
    openingTax: { type: 'money', list: false, form: { span: 4, section: 'opening' } },
    openingSso: { type: 'money', list: false, form: { span: 4, section: 'opening' } },
    /** The allowances it gives for the year, with the personal one (server-set) */
    total: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 1 } },
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
  },
  title: 'employeeId',
  columns: ['employeeId', 'year', 'total', 'spouse', 'children', 'parents'],
  sections: ['main', 'family', 'insurance', 'other', 'opening'],
  defaultSort: '-year',
  actions: ['read', 'create', 'update', 'delete', 'export'],
  ownerField: 'userId',
})

/**
 * payroll.payrollSetting (ตั้งค่าเงินเดือน): one record — social security's rate and wage bounds (by
 * the ministerial regulation of the year), the employer's account for the SSO filing, and whether
 * lateness is deducted.
 */
export const payrollSetting = defineResource({
  app: 'payroll',
  name: 'payrollSetting',
  schema: z.object({
    ssoRate: z.number().min(0).max(20).default(5),
    ssoMinWage: z.number().min(0).max(1_000_000).default(1650),
    ssoMaxWage: z.number().min(0).max(1_000_000).default(17_500),
    /** The employer's social security account (10 digits) and branch (6) */
    ssoAccount: z
      .string()
      .regex(/^\d{10}$/)
      .nullish(),
    ssoBranch: z
      .string()
      .regex(/^\d{6}$/)
      .nullish(),
    /** The day of the month time is counted to for its run (26th of last month – 25th): empty, the month's last */
    cutoffDay: z.number().int().min(1).max(28).nullish(),
    /** Lateness taken from monthly pay by the minute (off: not deducted) */
    deductLate: z.boolean().default(false),
    /** A salary advance: up to this share of the pay earned so far in the period (0: none) */
    advancePercent: z.number().min(0).max(100).default(50),
  }),
  fields: {
    ssoRate: { type: 'percent', form: { span: 4, section: 'sso' } },
    ssoMinWage: { type: 'money', form: { span: 4, section: 'sso' } },
    ssoMaxWage: { type: 'money', form: { span: 4, section: 'sso' } },
    ssoAccount: { form: { span: 6, section: 'sso' } },
    ssoBranch: { form: { span: 6, section: 'sso' } },
    cutoffDay: { type: 'integer', form: { span: 6, section: 'run' } },
    deductLate: { form: { span: 12, section: 'time' } },
    advancePercent: { type: 'percent', form: { span: 6, section: 'advance' } },
  },
  title: 'ssoRate',
  sections: ['run', 'sso', 'time', 'advance'],
  actions: ['read', 'update'],
})

export type PayrollSettings = Pick<z.infer<typeof payrollSetting.schema>, 'ssoRate' | 'ssoMinWage' | 'ssoMaxWage' | 'deductLate'> & {
  advancePercent?: number
  cutoffDay?: number | null
}
export const defaultPayrollSettings: PayrollSettings = { ssoRate: 5, ssoMinWage: 1650, ssoMaxWage: 17_500, deductLate: false }

// ─── Calculation ────────────────────────────────────────────────────────────────────────────────

/** Money to the satang */
export const roundMoney = (n: number) => Math.round(n * 100) / 100

/** The last day of a month (YYYY-MM) */
export function monthEnd(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}

/** The month before or after (YYYY-MM) */
export function monthAdd(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const d = new Date(Date.UTC(y, m - 1 + by, 1))
  return d.toISOString().slice(0, 7)
}

/** A calendar month's days: a regular run's salary */
export const monthDays = (month: string) => ({ start: `${month}-01`, end: monthEnd(month) })

/**
 * The days a month's regular run counts time for, by the cut-off day: the month itself without one
 * (or a cut-off of the 28th and later is not allowed: February), else from the day after the last
 * month's cut-off to this one's (cut-off 25: 26 November – 25 December for December)
 */
export function regularWindow(month: string, cutoffDay?: number | null): { start: string; end: string } {
  if (!cutoffDay) return monthDays(month)
  const day = String(cutoffDay).padStart(2, '0')
  return { start: addDays(`${monthAdd(month, -1)}-${day}`, 1), end: `${month}-${day}` }
}

/** The month whose regular run counts a date's time (cut-off 25: the 26th on is the next month's) */
export function monthCounting(date: string, cutoffDay?: number | null): string {
  const month = date.slice(0, 7)
  if (!cutoffDay || Number(date.slice(8, 10)) <= cutoffDay) return month
  return monthAdd(month, 1)
}

/**
 * How many regular runs are left in the month's year, this one included — the tax of the year
 * still to take is spread over them (October: 3)
 */
export const runsLeftIn = (month: string) => 12 - Number(month.slice(5, 7)) + 1

/** Days from one date to another, both counted */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
}

/**
 * The day rate: a day wage as it is; a monthly salary ÷ 30 (the Labour Protection Act's section
 * 68 for overtime, and the rate absence and unpaid leave are taken at)
 */
export const dayRateOf = (basis: PayBasis, rate: number) => (basis === 'daily' ? rate : rate / 30)

/** The hour rate: the day rate ÷ the normal hours of a working day */
export const hourRateOf = (basis: PayBasis, rate: number, normalHours: number) =>
  dayRateOf(basis, rate) / (normalHours > 0 ? normalHours : 8)

/**
 * A monthly salary for the days employed in the period: in full when employed all of it, else
 * by the day (÷ 30), never more than the salary
 */
export function salaryFor(rate: number, period: { start: string; end: string }, employed: { start?: string | null; end?: string | null }) {
  const from = employed.start && employed.start > period.start ? employed.start : period.start
  const to = employed.end && employed.end < period.end ? employed.end : period.end
  if (to < from) return { days: 0, amount: 0 }
  const days = daysBetween(from, to)
  if (days >= daysBetween(period.start, period.end)) return { days, amount: rate }
  return { days, amount: roundMoney(Math.min(rate, (rate / 30) * days)) }
}

/** A day's overtime as attendance counts it, with why the day was off */
export interface OvertimeDay {
  off?: 'dayOff' | 'holiday'
  overtime: number
  holidayWork: number
  holidayOvertime: number
}

/**
 * Overtime pay by the Labour Protection Act: overtime on a working day 1.5 × the hour rate
 * (section 61); work on a day off — 1 × more for monthly staff, paid for the day already, and for
 * day workers on a public holiday (paid for it too), 2 × for day workers on their weekly day off
 * (section 62); overtime on a day off 3 × (section 63). Lines by kind, with the hours.
 */
export function overtimePay(basis: PayBasis, hourRate: number, days: readonly OvertimeDay[]) {
  let overtime = 0
  let holidayOnce = 0
  let holidayTwice = 0
  let holidayOvertime = 0
  for (const d of days) {
    overtime += d.overtime
    holidayOvertime += d.holidayOvertime
    if (basis === 'daily' && d.off !== 'holiday') holidayTwice += d.holidayWork
    else holidayOnce += d.holidayWork
  }
  const lines: { code: 'OT' | 'HOLIDAY' | 'HOLIDAY_OT'; hours: number; times: number; amount: number }[] = []
  const add = (code: 'OT' | 'HOLIDAY' | 'HOLIDAY_OT', hours: number, times: number) => {
    if (hours > 0) lines.push({ code, hours, times, amount: roundMoney(hours * hourRate * times) })
  }
  add('OT', overtime, 1.5)
  add('HOLIDAY', holidayOnce, 1)
  add('HOLIDAY', holidayTwice, 2)
  add('HOLIDAY_OT', holidayOvertime, 3)
  return lines
}

/** The trip rate row for a job: the vehicle type's own rows, else those without a type; the band its distance falls in */
export function tripRateFor<R extends { vehicleType?: string | null; fromKm: number }>(
  rates: readonly R[],
  vehicleType: string | null | undefined,
  distanceKm: number | null | undefined,
): R | null {
  const own = vehicleType ? rates.filter((r) => r.vehicleType === vehicleType) : []
  const rows = own.length ? own : rates.filter((r) => !r.vehicleType)
  const km = distanceKm ?? 0
  let best: R | null = null
  for (const r of rows) if (r.fromKm <= km && (!best || r.fromKm > best.fromKm)) best = r
  return best
}

/** The rate row for a vehicle: its type's own, else the one without a type */
const ofVehicle = <R extends { vehicleType?: string | null }>(rows: readonly R[], vehicleType: string | null | undefined) =>
  (vehicleType ? rows.find((r) => r.vehicleType === vehicleType) : undefined) ?? rows.find((r) => !r.vehicleType) ?? null

/** The fuel rate row of a vehicle (`fuelRate`) */
export const fuelRateFor = <R extends { vehicleType?: string | null }>(rates: readonly R[], vehicleType: string | null | undefined) =>
  ofVehicle(rates, vehicleType)

/** A delivery run's fuel pay: its distance at the rate, to the satang; null while either is missing */
export const fuelPayOf = (distanceKm: number | null | undefined, perKm: number | null | undefined) =>
  distanceKm == null || perKm == null ? null : Math.round(distanceKm * perKm * 100) / 100

/**
 * Social security for a run: the rate of the month's wage (between the bounds), less what was
 * taken earlier in the month (an earlier run paying the same month) — whole baht, 50 satang up
 */
export function socialSecurityFor(settings: PayrollSettings, wage: number, earlier: { wage: number; sso: number } = { wage: 0, sso: 0 }) {
  const month = Math.min(Math.max(wage + earlier.wage, settings.ssoMinWage), settings.ssoMaxWage)
  const due = Math.floor((month * settings.ssoRate) / 100 + 0.5)
  return Math.max(0, due - earlier.sso)
}

/** Personal income tax by the year's brackets (from 2017) */
export const taxBrackets: readonly { upTo: number; rate: number }[] = [
  { upTo: 150_000, rate: 0 },
  { upTo: 300_000, rate: 5 },
  { upTo: 500_000, rate: 10 },
  { upTo: 750_000, rate: 15 },
  { upTo: 1_000_000, rate: 20 },
  { upTo: 2_000_000, rate: 25 },
  { upTo: 5_000_000, rate: 30 },
  { upTo: Number.POSITIVE_INFINITY, rate: 35 },
]

/** The tax on a year's net income */
export function incomeTax(net: number): number {
  let tax = 0
  let from = 0
  for (const b of taxBrackets) {
    if (net <= from) break
    tax += ((Math.min(net, b.upTo) - from) * b.rate) / 100
    from = b.upTo
  }
  return roundMoney(tax)
}

export type TaxDeclarationValues = Partial<
  Pick<
    z.infer<typeof taxDeclaration.schema>,
    | 'spouse'
    | 'children'
    | 'childrenFrom2018'
    | 'parents'
    | 'disabled'
    | 'lifeInsurance'
    | 'healthInsurance'
    | 'parentsHealthInsurance'
    | 'providentFund'
    | 'homeLoanInterest'
    | 'other'
  >
>

export const PERSONAL_ALLOWANCE = 60_000

/**
 * The allowances a declaration gives for the year, the personal one included: spouse 60,000;
 * a child 30,000 (the second on, born from 2018: 60,000); a parent 30,000; a disabled dependant
 * 60,000; life insurance up to 100,000 and health up to 25,000 (together up to 100,000); parents'
 * health up to 15,000; provident fund up to 500,000; home loan interest up to 100,000
 */
export function taxAllowances(d: TaxDeclarationValues | null | undefined): number {
  if (!d) return PERSONAL_ALLOWANCE
  const from2018 = Math.min(d.childrenFrom2018 ?? 0, d.children ?? 0)
  const health = Math.min(d.healthInsurance ?? 0, 25_000)
  const insurance = Math.min(Math.min(d.lifeInsurance ?? 0, 100_000) + health, 100_000)
  return (
    PERSONAL_ALLOWANCE +
    (d.spouse ? 60_000 : 0) +
    ((d.children ?? 0) - from2018) * 30_000 +
    from2018 * 60_000 +
    Math.min(d.parents ?? 0, 4) * 30_000 +
    (d.disabled ?? 0) * 60_000 +
    insurance +
    Math.min(d.parentsHealthInsurance ?? 0, 15_000) +
    Math.min(d.providentFund ?? 0, 500_000) +
    Math.min(d.homeLoanInterest ?? 0, 100_000) +
    (d.other ?? 0)
  )
}

/**
 * Withholding tax for a run, the Revenue Department's way: the year's income is what was paid so
 * far plus this run's regular taxable income for every run left; less the expense deduction (50%,
 * up to 100,000), the allowances and the year's social security; the tax on it, less what was
 * taken already, spread over the runs left. Irregular income (a bonus, severance) is not
 * repeated: the tax it adds to the year is taken whole in this run.
 */
export function withholdingFor(input: {
  /** This run's regular taxable income */
  taxable: number
  /** This run's irregular taxable income */
  irregular?: number
  sso: number
  ytdTaxable: number
  ytdTax: number
  ytdSso: number
  runsLeft: number
  allowances: number
}): number {
  const runs = Math.max(1, input.runsLeft)
  const sso = input.ytdSso + input.sso * runs
  const taxOn = (income: number) => incomeTax(Math.max(0, income - Math.min(income * 0.5, 100_000) - input.allowances - sso))
  const regular = input.ytdTaxable + Math.max(0, input.taxable) * runs
  const onRegular = taxOn(regular)
  const onAll = taxOn(regular + Math.max(0, input.irregular ?? 0))
  const due = (onRegular - input.ytdTax) / runs + (onAll - onRegular)
  return Math.max(0, roundMoney(due))
}

/** A payslip's totals from its lines; `irregular`: the part of `taxable` paid once (a bonus, severance) */
export function payslipTotals(lines: readonly (Pick<PayLine, 'kind' | 'amount' | 'taxable' | 'wage'> & { irregular?: boolean | null })[]) {
  let gross = 0
  let deductions = 0
  let taxable = 0
  let irregular = 0
  let wage = 0
  for (const l of lines) {
    const sign = l.kind === 'earning' ? 1 : -1
    if (l.kind === 'earning') gross += l.amount
    else deductions += l.amount
    if (l.taxable) taxable += sign * l.amount
    if (l.taxable && l.irregular) irregular += sign * l.amount
    if (l.wage) wage += sign * l.amount
  }
  const totalTaxable = roundMoney(Math.max(0, taxable))
  return {
    gross: roundMoney(gross),
    deductions: roundMoney(deductions),
    net: roundMoney(gross - deductions),
    taxable: totalTaxable,
    irregular: roundMoney(Math.min(totalTaxable, Math.max(0, irregular))),
    ssoWage: roundMoney(Math.max(0, wage)),
  }
}

/** Taken after tax but not limited by section 76: tax and social security (by law), an advance (wages paid early, not a debt) */
const notLimited = new Set(['SSO', 'TAX', 'ADVANCE'])

/**
 * Deductions the Labour Protection Act limits (section 76): those taken after tax other than tax,
 * social security and advances — together no more than a fifth of the pay due
 */
export function overDeduction(lines: readonly Pick<PayLine, 'code' | 'kind' | 'amount' | 'taxable' | 'wage'>[]) {
  const { gross } = payslipTotals(lines)
  const limited = lines
    .filter((l) => l.kind === 'deduction' && !l.taxable && !l.wage && !notLimited.has(l.code))
    .reduce((sum, l) => sum + l.amount, 0)
  return limited > gross / 5
}

/**
 * Section 76: each kind of deduction it limits — the security deposit among them — takes no more than a tenth of the
 * pay due (together no more than a fifth: `overDeduction`)
 */
export const bondDeductionCap = (gross: number) => roundMoney(Math.max(gross, 0) / 10)

/** The Ministry of Labour's announcement on employment guarantees: a cash deposit is at most 60 days' wages */
export const BOND_TARGET_DAYS = 60

/** The most a security deposit may be: 60 times the average day's wage (a monthly salary ÷ 30, or the day wage) */
export const bondTargetLimit = (basis: PayBasis, rate: number) => roundMoney(BOND_TARGET_DAYS * dayRateOf(basis, rate))

/**
 * Pay for annual leave not taken when someone leaves (section 67): the days left × the day rate.
 * By law, on resigning only the days carried over are owed; payroll removes the line when so.
 */
export const leavePayout = (basis: PayBasis, rate: number, daysLeft: number) => roundMoney(Math.max(0, daysLeft) * dayRateOf(basis, rate))

/** Severance by length of service (section 118): from this many full years, this many days of the last wage */
export const severanceTiers: readonly { years: number; days: number }[] = [
  { years: 20, days: 400 },
  { years: 10, days: 300 },
  { years: 6, days: 240 },
  { years: 3, days: 180 },
  { years: 1, days: 90 },
]

/** The date some full years after another (Feb 29 → Feb 28) */
function addYears(date: string, years: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const last = new Date(Date.UTC(y + years, m, 0)).getUTCDate()
  return `${y + years}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`
}

/**
 * Days of severance for someone let go (section 118): none under 120 days of service, 30 up to a
 * year, then by the full years served to their last day
 */
export function severanceDays(startDate: string, lastDay: string): number {
  if (daysBetween(startDate, lastDay) < 120) return 0
  const after = addDays(lastDay, 1)
  for (const t of severanceTiers) if (addYears(startDate, t.years) <= after) return t.days
  return 30
}

/**
 * Severance in baht and the part free of tax: severance under the labour law is exempt up to 300
 * days of the last wage and 300,000 baht; the rest is taxed as irregular income
 */
export function severancePay(basis: PayBasis, rate: number, days: number) {
  const dayRate = dayRateOf(basis, rate)
  const amount = roundMoney(days * dayRate)
  const exempt = roundMoney(Math.min(amount, 300 * dayRate, 300_000))
  return { amount, exempt, taxed: roundMoney(amount - exempt) }
}

/**
 * Whether an adjustment applies to a month's regular run: a once one in the month of its date, a
 * monthly one in every month from its date's to its end date's
 */
export function adjustmentApplies(
  adjustment: { repeat: (typeof payRepeats)[number]; date: string; endDate?: string | null },
  month: string,
): boolean {
  if (adjustment.repeat === 'once') return adjustment.date.slice(0, 7) === month
  if (month < adjustment.date.slice(0, 7)) return false
  return !adjustment.endDate || month <= adjustment.endDate.slice(0, 7)
}

// ─── Filings ────────────────────────────────────────────────────────────────────────────────────

/** A full name as first name and surname (the first word, then the rest), as filings want them */
export function splitName(name: string): { firstName: string; lastName: string } {
  const [first = '', ...rest] = name.trim().split(/\s+/)
  return { firstName: first, lastName: rest.join(' ') }
}

/**
 * Titles as codes in the SSO 1-10 text file. Taken from published layouts, not an official
 * table: check them on the first upload to e-Service.
 */
export const ssoTitleCodes: Record<(typeof nameTitles)[number], string> = { mr: '003', miss: '004', mrs: '005' }

/** One insured person's month in the SSO 1-10 filing */
export interface SsoRow {
  idCard: string
  title?: keyof typeof ssoTitleCodes | null
  firstName: string
  lastName: string
  /** The wage contributions are taken on (within the bounds) */
  wage: number
  contribution: number
}

const fit = (text: string, width: number) => text.slice(0, width).padEnd(width, ' ')
const digits = (n: number, width: number) =>
  String(Math.max(0, Math.round(n)))
    .padStart(width, '0')
    .slice(-width)
const satang = (n: number, width: number) => digits(Math.round(n * 100), width)
/** ddMMyy / MMyy in the Buddhist era */
const beDate = (date: string) => `${date.slice(8, 10)}${date.slice(5, 7)}${String(Number(date.slice(0, 4)) + 543).slice(-2)}`
const beMonth = (month: string) => `${month.slice(5, 7)}${String(Number(month.slice(0, 4)) + 543).slice(-2)}`

/**
 * The SSO 1-10 text file for a month (135 characters a line: a header, then one line per
 * person), by the layout published for e-Service uploads. Amounts are in satang without a point.
 * Encode it as TIS-620 (Windows-874) to upload.
 */
export function ssoFileLines(
  header: { account: string; branch: string; payDate: string; month: string; company: string; rate: number },
  rows: readonly SsoRow[],
): string[] {
  const wages = rows.reduce((sum, r) => sum + r.wage, 0)
  const employee = rows.reduce((sum, r) => sum + r.contribution, 0)
  const head = [
    '1',
    digits(Number(header.account), 10),
    digits(Number(header.branch), 6),
    beDate(header.payDate),
    beMonth(header.month),
    fit(header.company, 45),
    satang(header.rate, 4),
    digits(rows.length, 6),
    satang(wages, 15),
    satang(employee * 2, 14),
    satang(employee, 12),
    satang(employee, 12),
  ].join('')
  const lines = rows.map((r) =>
    [
      '2',
      fit(r.idCard, 13),
      fit(r.title ? ssoTitleCodes[r.title] : '', 3),
      fit(r.firstName, 30),
      fit(r.lastName, 35),
      satang(r.wage, 14),
      satang(r.contribution, 12),
      fit('', 27),
    ].join(''),
  )
  return [head, ...lines]
}

/** Text as TIS-620 bytes (Thai letters U+0E01–U+0E5B to 0xA1–0xFB; anything else outside ASCII becomes ?) */
export function tis620(text: string): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    out[i] = c < 0x80 ? c : c >= 0x0e01 && c <= 0x0e5b ? c - 0x0e00 + 0xa0 : 0x3f
  }
  return out
}
